import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Percent, Users as UsersIcon, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { gbp } from "../../lib/format";
import { Card, NumberInput, PrimaryButton, ToggleGroup } from "../../components/UI";

/* LT-31 — "we now charge £300 a day across the board". Tick the trades, say
 * the new figure (or a % change), see every before → after, apply once. */
const TIER_OPTS = [{ value: "charge", label: "Charge rate (Tier 1)" }, { value: "pay", label: "Pay rate (Tier 2)" }];
const MODE_OPTS = [{ value: "set", label: "Set to" }, { value: "percent", label: "Change by %" }];
const BASIS_OPTS = [{ value: "daily", label: "/day" }, { value: "hourly", label: "/hr" }];
const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

function projected(rate, { tier, mode, basis, value }, hpd) {
  const pay = tier === "pay";
  const cur = { hourly: Number(pay ? rate.actual_hourly_rate : rate.hourly_rate) || 0, daily: Number(pay ? rate.actual_day_rate : rate.day_rate) || 0,
                primary: (pay ? rate.actual_primary_field : rate.primary_field) || "hourly" };
  if (value == null || Number.isNaN(Number(value))) return { cur, next: null };
  if (mode === "set") {
    if (Number(value) <= 0) return { cur, next: null };
    return { cur, next: basis === "daily"
      ? { daily: r2(value), hourly: hpd ? r2(value / hpd) : 0 }
      : { hourly: r2(value), daily: r2(value * hpd) } };
  }
  const f = 1 + Number(value) / 100;
  if (Number(value) <= -100 || Math.abs(Number(value)) < 0.005) return { cur, next: null };
  const p = cur.primary === "daily" ? r2(cur.daily * f) : r2(cur.hourly * f);
  return { cur, next: cur.primary === "daily" ? { daily: p, hourly: hpd ? r2(p / hpd) : 0 } : { hourly: p, daily: r2(p * hpd) } };
}

export function LabourRatesBulkTab() {
  const [rates, setRates] = useState(null);
  const [hpd, setHpd] = useState(8);
  const [sel, setSel] = useState(() => new Set());
  const [opts, setOpts] = useState({ tier: "charge", mode: "set", basis: "daily", value: null });
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState(null);

  const load = () => api.get("/labour-rates").then((r) => setRates(r.data || [])).catch(() => setRates([]));
  useEffect(() => {
    load();
    api.get("/settings/working-day").then((r) => setHpd(Number(r.data?.hours_per_day || 8))).catch(() => {});
  }, []);

  const allIds = useMemo(() => (rates || []).map((r) => r.id), [rates]);
  const allOn = allIds.length > 0 && allIds.every((id) => sel.has(id));
  const toggleAll = () => setSel(allOn ? new Set() : new Set(allIds));
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const rows = useMemo(() => (rates || []).map((r) => ({ r, ...projected(r, opts, hpd) })), [rates, opts, hpd]);
  const changing = rows.filter(({ r, cur, next }) => sel.has(r.id) && next && (next.hourly !== cur.hourly || next.daily !== cur.daily));
  const canApply = !busy && sel.size > 0 && rows.some(({ next }) => next) && changing.length > 0;

  const apply = async () => {
    const what = opts.mode === "set"
      ? `set the ${opts.tier === "pay" ? "pay" : "charge"} rate to ${gbp(opts.value)}${opts.basis === "daily" ? "/day" : "/hr"}`
      : `change the ${opts.tier === "pay" ? "pay" : "charge"} rate by ${opts.value > 0 ? "+" : ""}${opts.value}%`;
    if (!window.confirm(`This will ${what} on ${changing.length} trade${changing.length === 1 ? "" : "s"}. Library items using them will be flagged for review. Continue?`)) return;
    setBusy(true);
    try {
      const res = await api.post("/labour-rates/bulk-rate", { ids: changing.map(({ r }) => r.id), tier: opts.tier, mode: opts.mode,
        basis: opts.mode === "set" ? opts.basis : undefined, value: Number(opts.value) });
      setLast(res.data);
      toast.success(`${res.data.updated} trade${res.data.updated === 1 ? "" : "s"} updated`);
      setSel(new Set());
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Bulk update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col gap-5" data-testid="labour-rates-bulk-tab">
      <Card className="p-5">
        <h3 className="font-display text-ink mb-1 inline-flex items-center gap-2" style={{ fontSize: 14, fontWeight: 600 }}>
          <UsersIcon size={14} className="text-ink-3" /> Update labour rates across the board
        </h3>
        <p className="text-ink-3 mb-4" style={{ fontSize: 12 }}>
          Tick the trades, give the new figure, check the before → after column, apply once. Each trade is written exactly as if you had typed
          it on its own page — the other side (hourly ↔ day) follows the Working Day of <span className="font-mono tnum">{hpd}h</span>,
          price history is kept, and library items using the trade are flagged for review.
        </p>
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <div className="col-head mb-1" style={{ fontSize: 9.5 }}>Which rate</div>
            <ToggleGroup value={opts.tier} onChange={(v) => setOpts((o) => ({ ...o, tier: v }))} options={TIER_OPTS} />
          </div>
          <div>
            <div className="col-head mb-1" style={{ fontSize: 9.5 }}>How</div>
            <ToggleGroup value={opts.mode} onChange={(v) => setOpts((o) => ({ ...o, mode: v, value: null }))} options={MODE_OPTS} />
          </div>
          <div style={{ width: 170 }}>
            <div className="col-head mb-1" style={{ fontSize: 9.5 }}>{opts.mode === "set" ? "New rate" : "Change"}</div>
            <NumberInput value={opts.value} onChange={(v) => setOpts((o) => ({ ...o, value: v }))} showZero={false}
              prefix={opts.mode === "set" ? "£" : undefined} suffix={opts.mode === "set" ? (opts.basis === "daily" ? "/day" : "/hr") : "%"}
              placeholder={opts.mode === "set" ? "300" : "5"} data-testid="labour-bulk-value" />
          </div>
          {opts.mode === "set" && (
            <div>
              <div className="col-head mb-1" style={{ fontSize: 9.5 }}>Basis</div>
              <ToggleGroup value={opts.basis} onChange={(v) => setOpts((o) => ({ ...o, basis: v }))} options={BASIS_OPTS} />
            </div>
          )}
          <div className="ml-auto" style={{ minWidth: 190 }}>
            <PrimaryButton icon={busy ? Loader2 : (opts.mode === "set" ? Save : Percent)} onClick={apply} disabled={!canApply} data-testid="labour-bulk-apply" className="w-full whitespace-nowrap">
              {changing.length ? `Update ${changing.length} trade${changing.length === 1 ? "" : "s"}` : "Update selected"}
            </PrimaryButton>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        {rates === null ? (
          <div className="p-5 text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : rates.length === 0 ? (
          <div className="p-5 text-ink-3" style={{ fontSize: 12 }}>No labour rates yet.</div>
        ) : (
          <table className="w-full" style={{ tableLayout: "fixed" }} data-testid="labour-bulk-table">
            {/* fixed columns: nothing moves when the "New" figures appear or the button label changes */}
            <colgroup>
              <col style={{ width: 44 }} />
              <col />
              <col style={{ width: 240 }} />
              <col style={{ width: 240 }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr className="border-b border-line">
                <th className="px-4 py-2 text-left">
                  <input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="Select all trades" data-testid="labour-bulk-select-all" />
                </th>
                <th className="col-head text-left px-2 py-2">Trade</th>
                <th className="col-head text-right px-2 py-2">Current {opts.tier === "pay" ? "pay" : "charge"}</th>
                <th className="col-head text-right px-2 py-2">New</th>
                <th className="col-head text-right px-4 py-2">Last changed</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ r, cur, next }) => {
                const on = sel.has(r.id);
                const moves = on && next && (next.hourly !== cur.hourly || next.daily !== cur.daily);
                const stamp = opts.tier === "pay" ? r.pay_rate_updated_at : r.charge_rate_updated_at;
                return (
                  <tr key={r.id} className={["border-b border-line-soft", on ? "bg-accent-soft/40" : ""].join(" ")} data-testid={`labour-bulk-row-${r.id}`}>
                    <td className="px-4 py-2"><input type="checkbox" checked={on} onChange={() => toggle(r.id)} aria-label={`Select ${r.trade_name}`} data-testid={`labour-bulk-check-${r.id}`} /></td>
                    <td className="px-2 py-2 text-ink" style={{ fontSize: 12.5 }}>
                      <button type="button" onClick={() => toggle(r.id)} className="text-left hover:text-accent">{r.trade_name}</button>
                      {r.trade_code && <span className="ml-2 font-mono text-ink-4" style={{ fontSize: 10.5 }}>{r.trade_code}</span>}
                    </td>
                    <td className="px-2 py-2 text-right font-mono tnum text-ink-2" style={{ fontSize: 12 }}>
                      {gbp(cur.daily)}<span className="text-ink-4">/day</span> <span className="text-ink-4">·</span> {gbp(cur.hourly)}<span className="text-ink-4">/hr</span>
                    </td>
                    <td className={["px-2 py-2 text-right font-mono tnum", moves ? "text-ink" : "text-ink-4"].join(" ")} style={{ fontSize: 12, fontWeight: moves ? 600 : 400 }}
                        data-testid={`labour-bulk-new-${r.id}`}>
                      {on && next ? (moves ? <>{gbp(next.daily)}<span className="text-ink-4">/day</span> <span className="text-ink-4">·</span> {gbp(next.hourly)}<span className="text-ink-4">/hr</span></> : "no change") : "—"}
                    </td>
                    <td className="px-4 py-2 text-right text-ink-4" style={{ fontSize: 11 }}>{stamp ? new Date(stamp).toLocaleDateString("en-GB") : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {last && (
        <Card className="p-4" data-testid="labour-bulk-result">
          <div className="text-ink mb-1" style={{ fontSize: 12.5, fontWeight: 600 }}>Done — {last.updated} trade{last.updated === 1 ? "" : "s"} updated{last.skipped?.length ? `, ${last.skipped.length} skipped` : ""}</div>
          <ul className="text-ink-3" style={{ fontSize: 11.5 }}>
            {last.results.map((x) => (
              <li key={x.id} className="font-mono tnum">{x.trade_name}: {gbp(x.before.daily)}/day → {gbp(x.after.daily)}/day ({gbp(x.after.hourly)}/hr)</li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
